import { Injectable, effect, inject } from '@angular/core';
import { CanDeactivateFn } from '@angular/router';
import { environment } from 'src/environments/environment';
import { ApiService } from 'src/app/services/api.service';
import { SharedService } from 'src/app/services/shared.service';
import { localStorageService } from 'src/app/services/localstorage.service';
import { FmTerminalSocket } from './fm-terminal.socket';

/**
 * One flow's claim on a reader: a payment, enrolment or contract the panel has
 * handed to it and is waiting on.
 *
 * The flow owns the session, so it describes it through getters rather than
 * telling the service about every start and finish — the three flows each end a
 * session in four or five places, and a registry that had to hear about all of
 * them would be wrong the first time one was missed.
 */
export interface TerminalHandoff {
  /** For the log only. */
  flow: string;
  /** The reader the session is on. */
  trId: () => number | null;
  /** The session while the customer is still at the reader, otherwise null. */
  liveTsId: () => number | null;
  /**
   * Takes the session back off the reader and clears the flow's own state.
   * The flow's existing Cancel, so leaving ends the same way pressing it does.
   */
  cancel: () => void;
}

/**
 * Screens on which the customer, not the panel, has the reader.
 *
 * `card_prompt` is the one asked for. `processing` is added because it is what
 * follows a card tap: the charge is running, and a cancel landing then is the
 * one that can leave a customer charged with the panel having forgotten.
 */
export const TERMINAL_CUSTOMER_SCREENS = ['card_prompt', 'processing'];

/* Session ids this tab was holding when it went away, so the next load can
   finish the job if the request sent on the way out never arrived. Per tab on
   purpose: localStorage would let a reload in one tab cancel a customer's
   payment that another tab's staff member is watching. */
const ORPHAN_KEY = 'fm_terminal_orphaned_sessions';

/* Older than the session's own lifetime (15 minutes, server-side). Past that the
   sweeper has closed it, and asking again only adds a request. */
const ORPHAN_MAX_AGE_MS = 20 * 60 * 1000;

const BLOCKED_NOTICE_GAP_MS = 2000;

/**
 * Keeps staff from walking away from a reader that has a customer at it.
 *
 * Without this, a session survives everything the panel can do to the page: Back
 * leaves the reader on its card prompt with an amount nobody is going to collect,
 * and a reload loses the panel's only handle on it. Three rules, the same for
 * every flow — `pp_terminal`, `subscription_terminal` and `contract_terminal`:
 *
 * 1. Leaving cancels. Back, a sidebar link, the wizard's own arrow, a reload or
 *    closing the tab all end the session, so the reader returns to idle.
 * 2. Except while the customer is at the card. From `card_prompt` until the
 *    reader reports back, going back is refused — by the router, by the
 *    wizard's arrow, and by the browser's own Back button and Alt+Left. A reload
 *    cannot be refused, only questioned, so that one asks first and then cancels.
 * 3. The panel's own Cancel is not "going back" and stays available. The way out
 *    of a customer who has walked away is a button, not a hunt for a shortcut.
 *
 * Whether the customer is at the card comes from the reader's own frame, which
 * the panel only hears while it is watching that reader. If no frame arrives the
 * lock never engages and leaving cancels — the choice that fails towards a free
 * reader rather than towards a panel nobody can leave.
 */
@Injectable({ providedIn: 'root' })
export class TerminalHandoffService {

  private readonly socket = inject(FmTerminalSocket);
  private readonly api = inject(ApiService);
  private readonly shared = inject(SharedService);
  private readonly local = inject(localStorageService);

  private readonly owners = new Set<TerminalHandoff>();

  /** Sessions already cancelled from here, so a second path does not repeat it. */
  private readonly cancelled = new Set<number>();

  /** The address the browser's history is being held at while locked. */
  private heldUrl: string | null = null;
  private lastNotice = 0;

  constructor() {
    window.addEventListener('beforeunload', event => this.askBeforeUnload(event));
    window.addEventListener('pagehide', () => this.cancelOnUnload());
    window.addEventListener('popstate', () => this.holdHistory());

    /* The browser's Back button, Alt+Left and the mouse's back button are all
       the same event, and the router answers it by moving first and checking
       afterwards. The guard below then undoes the move, which leaves the
       history one entry short — so a second press walks out of the panel.
       Putting an entry of our own in front of them keeps the first press on
       the same address, where the router has nothing to do. */
    effect(() => {
      this.socket.screen();
      if (this.isLocked()) {
        if (this.heldUrl === null) {
          this.heldUrl = window.location.href;
          history.pushState(history.state, '', this.heldUrl);
        }
      } else {
        this.heldUrl = null;
      }
    });
  }

  /**
   * Registers a flow's session. Returns what to call when the flow goes away,
   * which also takes back any session it still has live.
   */
  register(handoff: TerminalHandoff): () => void {
    this.owners.add(handoff);
    return () => {
      this.cancelOne(handoff);
      this.owners.delete(handoff);
    };
  }

  /**
   * Whether leaving is allowed, and if it is, takes every live session back.
   *
   * The one call every way out makes — the route guard, and the wizard's own
   * Back arrow — so they cannot disagree about it.
   */
  leave(): boolean {
    if (this.live().length === 0) { return true; }
    if (this.isLocked()) {
      this.tell();
      return false;
    }
    this.owners.forEach(owner => this.cancelOne(owner));
    return true;
  }

  /** Whether any live session has its customer at the card. */
  isLocked(): boolean {
    return this.live().some(({ owner, tsId }) => this.atTheCard(tsId, owner.trId()));
  }

  /** Cancels sessions a previous load of this tab left behind. Call once on start. */
  recoverOrphans(): void {
    const ids = this.readOrphans();
    try { sessionStorage.removeItem(ORPHAN_KEY); } catch { /* storage blocked: nothing to recover */ }

    ids.forEach(tsId => {
      /* Answered `already_finished` rather than with an error when the request
         sent on the way out did arrive, so this is quiet in the usual case. */
      this.api.api({ ts_id: tsId }, 'terminal/cancel_session', 'post', false)
        .subscribe({ error: () => undefined });
    });
  }

  private live(): { owner: TerminalHandoff; tsId: number }[] {
    const found: { owner: TerminalHandoff; tsId: number }[] = [];
    this.owners.forEach(owner => {
      const tsId = owner.liveTsId();
      /* One already being cancelled is on its way out, and counting it would
         refuse the very next Back for the seconds the reader takes to answer. */
      if (tsId && !this.cancelled.has(tsId)) { found.push({ owner, tsId }); }
    });
    return found;
  }

  private cancelOne(owner: TerminalHandoff): void {
    const tsId = owner.liveTsId();
    if (!tsId || this.cancelled.has(tsId)) { return; }
    this.cancelled.add(tsId);
    owner.cancel();
  }

  private atTheCard(tsId: number, trId: number | null): boolean {
    const frame = this.socket.screen();
    if (!frame || !TERMINAL_CUSTOMER_SCREENS.includes(frame.screen_name ?? '')) { return false; }
    if (trId && frame.tr_id && frame.tr_id !== trId) { return false; }
    // A frame that does not name its session is taken as the one we sent: the
    // reader only ever shows one.
    return !frame.ts_id || frame.ts_id === tsId;
  }

  /** Says why leaving is refused; at most once every couple of seconds. */
  tell(): void {
    const now = Date.now();
    if (now - this.lastNotice < BLOCKED_NOTICE_GAP_MS) { return; }
    this.lastNotice = now;
    this.shared.customPopups(
      'The customer is at the reader. You can leave once they finish, or press Cancel to take it back.',
      2,
    );
  }

  /** Runs after the browser has already moved; puts it back where it was. */
  private holdHistory(): void {
    if (!this.isLocked() || this.heldUrl === null) { return; }
    history.pushState(history.state, '', this.heldUrl);
    this.tell();
  }

  /**
   * A reload or a closed tab cannot be refused, only questioned. The browser
   * shows its own wording; only the fact that it asks can be chosen.
   */
  private askBeforeUnload(event: BeforeUnloadEvent): void {
    if (!this.isLocked()) { return; }
    event.preventDefault();
    event.returnValue = '';
  }

  /**
   * The page is going, for certain: a reload, a closed tab, or a link out.
   *
   * Sent with `keepalive` so the request outlives the page, and remembered
   * first so that if it does not — a browser that refuses a preflighted
   * keepalive, a connection that dropped — the next load cancels it instead.
   * The server's own expiry is the last backstop behind both.
   */
  private cancelOnUnload(): void {
    const ids = this.live().map(({ tsId }) => tsId);
    if (ids.length === 0) { return; }

    this.rememberOrphans(ids);
    ids.forEach(tsId => this.sendCancelKeepalive(tsId));
  }

  /* The headers InterceptorService adds to every HttpClient call. A request made
     while the page is unloading cannot go through HttpClient, so they are
     repeated here — keep the two in step if the interceptor's change. */
  private sendCancelKeepalive(tsId: number): void {
    const token = this.local.customGetItem('token');
    if (!token) { return; }
    const profile = this.local.customGetItem('userProfileData');

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      device_type: String(environment.device_type ?? ''),
      app_type: String(environment.app_type ?? ''),
      app_version: String(environment.app_version ?? ''),
      device_token: String(environment.device_token ?? ''),
      device_id: String(this.local.customGetItem('user') ?? ''),
      environment: String(environment.environment ?? ''),
      Authorization: token,
    };
    if (profile?.doctor_id) { headers['x-doctor-id'] = String(profile.doctor_id); }

    try {
      fetch(environment.baseUrl + 'terminal/cancel_session', {
        method: 'POST',
        keepalive: true,
        headers,
        body: JSON.stringify({ ts_id: tsId }),
      }).catch(() => undefined);
    } catch {
      // Nothing more can be done from a page that is closing.
    }
  }

  private rememberOrphans(ids: number[]): void {
    const at = Date.now();
    const kept = this.readOrphans().concat(ids);
    try {
      sessionStorage.setItem(ORPHAN_KEY, JSON.stringify(kept.map(tsId => ({ tsId, at }))));
    } catch {
      // Storage blocked: the keepalive request and the server's expiry remain.
    }
  }

  private readOrphans(): number[] {
    try {
      const raw = JSON.parse(sessionStorage.getItem(ORPHAN_KEY) || '[]');
      const now = Date.now();
      return (Array.isArray(raw) ? raw : [])
        .filter(entry => entry && Number(entry.tsId) && now - Number(entry.at) < ORPHAN_MAX_AGE_MS)
        .map(entry => Number(entry.tsId));
    } catch {
      return [];
    }
  }
}

/**
 * Route guard for the page the flows live on. Navigating away — Back, the
 * sidebar, a link — goes through `leave()`, so it is refused while the customer
 * is at the card and otherwise takes the session back.
 *
 * Navigations that stay on the page (a query string changing) are not leaving.
 */
export const leaveTerminalHandoffGuard: CanDeactivateFn<unknown> = (_component, _currentRoute, currentState, nextState) => {
  const path = (url: string) => url.split(/[?#]/)[0];
  if (path(currentState.url) === path(nextState.url)) { return true; }
  return inject(TerminalHandoffService).leave();
};
